import React, { useState, useEffect } from 'react';
import Navbar from '../../Navbar';
import Sidebar from '../../Sidebar';
import { BlockMath } from 'react-katex';
import '../../../styles/Tutorial1.css';

const ChemicalReactionEngineeringNotes = () => {
  const [activeSection, setActiveSection] = useState('what-is-cre');

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'what-is-cre',
        'chemical-identity',
        'reaction-rate',
        'mole-balance',
        'batch',
        'cstr',
        'pfr',
        'pbr',
        'reactor-summary',
        'pd-intro',
        'gas-conc',
        'ergun',
        'pd-deriv',
        'eps-zero',
        'profiles',
        'example1',
        'example2',
        'eng-analysis',
        'algorithm'
      ];

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(sectionId);
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>

      <Sidebar />

      <div style={{
        marginLeft:    '220px',
        width:         'calc(100% - 220px)',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        <Navbar activePage="app" />

        <div style={{
          flex:      1,
          marginTop: '68px',
          overflowY: 'auto',
          overflowX:  'hidden',
          minHeight: 0,
        }}>
    <div className="cre-container" style={{ background: 'var(--t-bg)', minHeight: '100%' }}>
      {/* MAIN CONTENT */}
      <main className="cre-main">
        <div className="cre-main-inner">
        {/* HERO */}
        <div className="cre-hero">
          <div className="cre-hero-tag">Open Course Notes</div>
          <h1>Chemical Reaction Engineering</h1>
          <p className="cre-hero-desc">A comprehensive study guide built from Prof. H. Scott Fogler's lecture notes — covering reactor design fundamentals through pressure drop analysis in packed beds.</p>
          <div className="cre-hero-meta">
            <span className="cre-meta-item"><span className="cre-meta-dot"></span>Based on Fogler's Elements of CRE, 6th Ed.</span>
            <span className="cre-meta-item"><span className="cre-meta-dot"></span>Lectures 1 &amp; 8</span>
            <span className="cre-meta-item"><span className="cre-meta-dot"></span>University of Michigan</span>
          </div>
        </div>

        {/* TOC */}
        <div className="cre-toc-box">
          <h3>Quick Navigation</h3>
          <div className="cre-toc-grid">
            <a className={`cre-toc-link ${activeSection === 'what-is-cre' ? 'cre-toc-link--active' : ''}`} href="#what-is-cre">What is CRE?</a>
            <a className={`cre-toc-link ${activeSection === 'batch' ? 'cre-toc-link--active' : ''}`} href="#batch">Batch Reactor</a>
            <a className={`cre-toc-link ${activeSection === 'reaction-rate' ? 'cre-toc-link--active' : ''}`} href="#reaction-rate">Reaction Rate</a>
            <a className={`cre-toc-link ${activeSection === 'cstr' ? 'cre-toc-link--active' : ''}`} href="#cstr">CSTR</a>
            <a className={`cre-toc-link ${activeSection === 'mole-balance' ? 'cre-toc-link--active' : ''}`} href="#mole-balance">General Mole Balance</a>
            <a className={`cre-toc-link ${activeSection === 'pfr' ? 'cre-toc-link--active' : ''}`} href="#pfr">Plug Flow Reactor</a>
            <a className={`cre-toc-link ${activeSection === 'ergun' ? 'cre-toc-link--active' : ''}`} href="#ergun">Ergun Equation</a>
            <a className={`cre-toc-link ${activeSection === 'pbr' ? 'cre-toc-link--active' : ''}`} href="#pbr">Packed Bed Reactor</a>
            <a className={`cre-toc-link ${activeSection === 'pd-deriv' ? 'cre-toc-link--active' : ''}`} href="#pd-deriv">Pressure Drop: dp/dW</a>
            <a className={`cre-toc-link ${activeSection === 'example1' ? 'cre-toc-link--active' : ''}`} href="#example1">Worked Example 1</a>
            <a className={`cre-toc-link ${activeSection === 'reactor-summary' ? 'cre-toc-link--active' : ''}`} href="#reactor-summary">Reactor Summary Table</a>
            <a className={`cre-toc-link ${activeSection === 'example2' ? 'cre-toc-link--active' : ''}`} href="#example2">Worked Example 2</a>
          </div>
        </div>

        {/* LECTURE 1 — FOUNDATIONS */}

        {/* SECTION: What is CRE */}
        <section className="cre-section" id="what-is-cre">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Introduction</div>
          <h2>What is Chemical Reaction Engineering?</h2>
          <div className="cre-section-divider"></div>
          <p><strong>Chemical Reaction Engineering (CRE)</strong> is the field that studies the <em>rates and mechanisms</em> of chemical reactions, and the <em>design of the reactors</em> in which they take place. It sits at the heart of virtually every chemical process and is what fundamentally separates chemical engineers from other engineering disciplines.</p>
          <p>CRE is applied wherever chemical transformations are important — from industrial chemical plants (like those run by Dow, DuPont, Chevron) to environmental systems (wetlands treating wastewater), pharmaceutical production (penicillin fermentation), and even the human body (drug metabolism, digestion).</p>
          <div style={{ margin: '24px 0', textAlign: 'center' }}>
            <img src="/ethylene-glycol-plant.jpg" alt="Industrial Chemical Plant for Ethylene Glycol Production showing reactors, distillation columns, and process flow" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
            <p style={{ fontSize: '12px', color: 'var(--t-text-dim)', marginTop: '8px' }}>Example: Industrial Chemical Plant for Ethylene Glycol Production</p>
          </div>
          <div className="cre-callout cre-callout--key">
            <p><strong>Core Question CRE Answers:</strong> Given a desired chemical transformation, how large should the reactor be, what conditions should it operate under, and what conversion of reactants can we achieve?</p>
          </div>
          <h3>The CRE Algorithm — Building Blocks</h3>
          <p>All CRE problems are solved using a consistent 4-step algorithm. These blocks <em>build upon each other</em> — you cannot skip any:</p>
          <div className="cre-card-grid">
            <div className="cre-card">
              <div className="cre-card-icon">⚖️</div>
              <h4>Block 1: Mole Balances</h4>
              <p>Accounting for moles of each species entering, leaving, reacting, and accumulating in a system volume.</p>
            </div>
            <div className="cre-card">
              <div className="cre-card-icon">⚗️</div>
              <h4>Block 2: Rate Laws</h4>
              <p>Mathematical expressions describing how fast a reaction proceeds as a function of concentration, temperature, and catalyst.</p>
            </div>
            <div className="cre-card">
              <div className="cre-card-icon">🔢</div>
              <h4>Block 3: Stoichiometry</h4>
              <p>Relating the concentrations of all species to conversion X, using stoichiometric coefficients and flow conditions.</p>
            </div>
            <div className="cre-card">
              <div className="cre-card-icon">🔗</div>
              <h4>Block 4: Combine &amp; Solve</h4>
              <p>Substituting Rate Law and Stoichiometry into the Mole Balance and solving — analytically or with an ODE solver.</p>
            </div>
          </div>
          <div className="cre-callout cre-callout--warn">
            <p><strong>Important:</strong> Unlike separation operations (distillation, filtration, adsorption) which are independent of each other, CRE topics build sequentially. Cutting corners on any block makes the entire analysis unreliable.</p>
          </div>
        </section>

        {/* SECTION: Chemical Identity */}
        <section className="cre-section" id="chemical-identity">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Fundamentals</div>
          <h2>Chemical Identity &amp; Reactions</h2>
          <div className="cre-section-divider"></div>
          <p>A <strong>chemical species</strong> is said to have <em>reacted</em> when it has lost its chemical identity. A species' identity is determined by the <strong>kind, number, and configuration</strong> of its atoms.</p>
          <h3>Three Ways a Species Loses Its Identity</h3>
          <ol className="cre-section ol">
            <li><strong>Decomposition</strong> — a molecule breaks apart:<br />
              <span className="cre-mono">CH₃CH₃ → H₂ + H₂C=CH₂</span>
            </li>
            <li><strong>Combination</strong> — two species combine:<br />
              <span className="cre-mono">N₂ + O₂ → 2NO</span>
            </li>
            <li><strong>Isomerization</strong> — the molecule rearranges:<br />
              <span className="cre-mono">C₂H₅CH=CH₂ → CH₂=C(CH₃)₂</span>
            </li>
          </ol>
          <h3>Forward vs. Reverse Reactions</h3>
          <p>Most reactions are <strong>reversible</strong>. In a <em>forward reaction</em>, reactants are consumed and products are formed. In a <em>reverse reaction</em>, products convert back to reactants. At <strong>chemical equilibrium</strong>, the rate of the forward reaction equals the rate of the reverse reaction — there is no net change in composition.</p>
          <div className="cre-callout cre-callout--tip">
            <p><strong>Example:</strong> For A → B, if the reaction is reversible: as [A] decreases and [B] increases over time, the forward rate slows down and the reverse rate speeds up. Eventually they balance — this is equilibrium. CRE helps us understand whether equilibrium or reaction kinetics limits our conversion.</p>
          </div>
        </section>

        {/* SECTION: Reaction Rate */}
        <section className="cre-section" id="reaction-rate">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Core Concept</div>
          <h2>Reaction Rate</h2>
          <div className="cre-section-divider"></div>
          <p>The <strong>reaction rate</strong> is the rate at which a species loses its chemical identity <em>per unit volume</em>. It has units of mol/dm³/s (or mol/L/s).</p>
          <h3>Sign Convention — Critical to Get Right</h3>
          <p>For the simple reaction <code className="cre-inline-code">A → B</code>:</p>
          <table className="cre-table">
            <thead>
              <tr><th>Symbol</th><th>Meaning</th><th>Sign</th></tr>
            </thead>
            <tbody>
              <tr><td><code className="cre-inline-code">r_A</code></td><td>Rate of <em>formation</em> of A per unit volume</td><td>Negative (A is consumed)</td></tr>
              <tr><td><code className="cre-inline-code">-r_A</code></td><td>Rate of <em>disappearance</em> of A per unit volume</td><td>Positive (always positive for reactant)</td></tr>
              <tr><td><code className="cre-inline-code">r_B</code></td><td>Rate of <em>formation</em> of B per unit volume</td><td>Positive (B is produced)</td></tr>
            </tbody>
          </table>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Example: A → B, r_B = 0.2 mol/dm³/s</div>
            <div className="cre-eq-main"><BlockMath math="r_B = 0.2\ \text{mol/dm}^3/\text{s} \implies -r_A = 0.2\ \text{mol/dm}^3/\text{s} \implies r_A = -0.2\ \text{mol/dm}^3/\text{s}" /></div>
          </div>
          <h3>Key Properties of the Rate Law</h3>
          <ul>
            <li><code className="cre-inline-code">r_j</code> is a function of <strong>concentration, temperature, pressure,</strong> and type of catalyst (if any).</li>
            <li><code className="cre-inline-code">r_j</code> is <strong>independent</strong> of the type of reactor (batch, CSTR, PFR, etc.).</li>
            <li><code className="cre-inline-code">r_j</code> is an <strong>algebraic equation</strong> (not a differential equation). Examples:<br />&nbsp;&nbsp;<code className="cre-inline-code">-r_A = kC_A</code> &nbsp;&nbsp; (first order)<br />&nbsp;&nbsp;<code className="cre-inline-code">-r_A = kC_A²</code> &nbsp;(second order)</li>
            <li><strong>dC_A/dt is NOT the rate of reaction</strong> — it is the rate of change of concentration in a batch reactor, which equals the rate only when volume is constant.</li>
          </ul>
          <h3>Catalytic Reactions</h3>
          <p>For reactions over a solid catalyst, we use <code className="cre-inline-code">-r'_A</code> (note the prime), which represents the rate of disappearance of A on a <strong>per mass of catalyst</strong> basis — units: mol/g_cat/s. This is used in Packed Bed Reactors (PBR).</p>
          <div className="cre-callout cre-callout--key">
            <p><strong>Rate Laws are determined experimentally</strong> — they cannot be derived from stoichiometry alone. The form <code className="cre-inline-code">-r_A = kC_A^α C_B^β</code> must be measured in the lab. The orders α and β are often (but not always) integers.</p>
          </div>
        </section>

        {/* SECTION: Mole Balance */}
        <section className="cre-section" id="mole-balance">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Building Block 1</div>
          <h2>General Mole Balance Equation</h2>
          <div className="cre-section-divider"></div>
          <p>The mole balance is the <em>foundation</em> of all reactor design. For any species j in a system volume V, we apply conservation of moles:</p>
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <img src="/mole-balance-diagram.jpg" alt="Mole Balance Diagram showing control volume with inlet F_j0, outlet F_j, generation G_j, and accumulation dN_j/dt" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Word Form (the golden equation)</div>
            <div className="cre-eq-main">
              <BlockMath math="F_{j0} - F_j + G_j = \frac{dN_j}{dt}" />
            </div>
            <div className="cre-eq-note">
              F_j0 = molar flow rate of j IN (mol/s) &nbsp;|&nbsp; F_j = molar flow rate of j OUT (mol/s)<br />
              G_j = molar rate of generation of j (mol/s) &nbsp;|&nbsp; N_j = total moles of j in system
            </div>
          </div>
          <h3>Computing G_j — the Generation Term</h3>
          <p>G_j depends on the reaction rate and the volume:</p>
          <ul>
            <li>If the system is <strong>spatially uniform</strong> (well-mixed): <code className="cre-inline-code">G_j = r_j · V</code></li>
            <li>If the rate varies with position (e.g., in a PFR): <code className="cre-inline-code">G_j = ∫ r_j dV</code></li>
          </ul>
          <p>The general mole balance (substituting G_j) becomes:</p>
          <div className="cre-eq-box">
            <div className="cre-eq-label">General Mole Balance on System Volume V</div>
            <div className="cre-eq-main"><BlockMath math="F_{A0} - F_A + \int r_A \, dV = \frac{dN_A}{dt}" /></div>
            <div className="cre-eq-note">This one equation, applied differently to each reactor type, gives all the reactor design equations below.</div>
          </div>
        </section>

        {/* SECTION: Batch Reactor */}
        <section className="cre-section" id="batch">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Reactor Type 1</div>
          <h2>Batch Reactor (BR)</h2>
          <div className="cre-section-divider"></div>
          <p>A <strong>batch reactor</strong> is a closed vessel — reactants are loaded, the reaction proceeds over time, and products are removed at the end. There is no continuous flow in or out during operation.</p>
          <h3>Physical Picture</h3>
          <p>Think of a large stirred tank (like a cooking pot). All reactants are added at t=0, and we wait until the desired conversion is achieved. Well-mixed → uniform concentration throughout.</p>
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <img src="/batch-reactor-diagram.jpg" alt="Batch Reactor Diagram showing closed vessel with stirrer and temperature measurement" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <div className="cre-callout cre-callout--key">
            <p><strong>Key Feature:</strong> In a batch reactor, F_A0 = F_A = 0 (no flow in or out). The only thing changing is the number of moles of A inside the tank.</p>
          </div>
          <div id="batch-derive">
            <h3>Deriving the Batch Reactor Equation</h3>
            <div className="cre-steps">
              <div className="cre-step">
                <div className="cre-step-num">1</div>
                <div className="cre-step-content">
                  <h4>Start with the General Mole Balance</h4>
                  <p>F_A0 − F_A + ∫ r_A dV = dN_A/dt</p>
                </div>
              </div>
              <div className="cre-step">
                <div className="cre-step-num">2</div>
                <div className="cre-step-content">
                  <h4>No flow in or out</h4>
                  <p>Set F_A0 = 0 and F_A = 0</p>
                </div>
              </div>
              <div className="cre-step">
                <div className="cre-step-num">3</div>
                <div className="cre-step-content">
                  <h4>Well-mixed assumption</h4>
                  <p>Since concentration is uniform, r_A is constant throughout V: &nbsp; ∫ r_A dV = r_A · V</p>
                </div>
              </div>
              <div className="cre-step">
                <div className="cre-step-num">4</div>
                <div className="cre-step-content">
                  <h4>Final batch reactor equation</h4>
                  <div className="cre-eq-box">
                    <div className="cre-eq-main"><BlockMath math="\frac{dN_A}{dt} = r_A \cdot V" /></div>
                  </div>
                </div>
              </div>
              <div className="cre-step">
                <div className="cre-step-num">5</div>
                <div className="cre-step-content">
                  <h4>Integral form — time to achieve conversion</h4>
                  <div className="cre-eq-box">
                    <div className="cre-eq-main"><BlockMath math="t = \int_{N_{A0}}^{N_A} \frac{dN_A}{-r_A \cdot V}" /></div>
                    <div className="cre-eq-note">This gives the time required to reduce moles of A from initial value N_A0 to final value N_A.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p>As the reaction proceeds in the batch, <strong>N_A decreases</strong> (A is consumed). The rate <code className="cre-inline-code">-r_A</code> also changes because concentration changes. This is why integration is needed.</p>
        </section>

        {/* SECTION: CSTR */}
        <section className="cre-section" id="cstr">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Reactor Type 2</div>
          <h2>Continuously Stirred Tank Reactor (CSTR)</h2>
          <div className="cre-section-divider"></div>
          <p>A <strong>CSTR</strong> (also called a "back-mix reactor") operates at <em>steady state</em> with continuous feed and product streams. It is well-mixed, meaning the composition inside the tank is <strong>identical</strong> to the exit composition at all times.</p>
          <h3>Physical Picture</h3>
          <p>Reactants flow in continuously, products flow out continuously. A stirrer ensures perfect mixing. Because it's perfectly mixed, the reaction happens at the exit concentration — which is lower than the feed. This makes the CSTR the least efficient reactor for positive-order reactions (the rate is slowest at exit conditions).</p>
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <img src="/cstr-diagram.jpg" alt="CSTR Diagram showing continuous inlet F_A0, outlet F_A, internal agitator and volume V" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <div id="cstr-derive">
            <h3>Deriving the CSTR Design Equation</h3>
            <div className="cre-steps">
              <div className="cre-step">
                <div className="cre-step-num">1</div>
                <div className="cre-step-content">
                  <h4>Steady state: no accumulation</h4>
                  <p>At steady state, dN_A/dt = 0. The general mole balance becomes: &nbsp; F_A0 − F_A + ∫ r_A dV = 0</p>
                </div>
              </div>
              <div className="cre-step">
                <div className="cre-step-num">2</div>
                <div className="cre-step-content">
                  <h4>Well-mixed → uniform rate</h4>
                  <p>∫ r_A dV = r_A · V</p>
                </div>
              </div>
              <div className="cre-step">
                <div className="cre-step-num">3</div>
                <div className="cre-step-content">
                  <h4>Solve for reactor volume</h4>
                  <div className="cre-eq-box">
                    <div className="cre-eq-label">CSTR Design Equation (Algebraic!)</div>
                    <div className="cre-eq-main"><BlockMath math="V = \frac{F_{A0} - F_A}{-r_A}" /></div>
                    <div className="cre-eq-note">The rate −r_A is evaluated at <em>exit conditions</em> (temperature and concentration of the outlet stream).</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="cre-callout cre-callout--warn">
            <p><strong>Key Insight:</strong> The CSTR gives an algebraic equation for V — no integration needed! The trade-off: because −r_A is evaluated at the lowest (exit) concentration, the CSTR requires a larger volume than a PFR for the same conversion.</p>
          </div>
        </section>

        {/* SECTION: PFR */}
        <section className="cre-section" id="pfr">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Reactor Type 3</div>
          <h2>Plug Flow Reactor (PFR)</h2>
          <div className="cre-section-divider"></div>
          <p>In a <strong>Plug Flow Reactor (PFR)</strong>, reactants flow through a tube and are converted as they move down the length. There is <em>no mixing</em> in the axial (flow) direction — each "plug" of fluid has the same residence time. Concentration and reaction rate <strong>vary along the length</strong> of the reactor.</p>
          <h3>Physical Picture</h3>
          <p>Imagine a long pipe. At the inlet, concentration of A is highest (rate is fastest). Moving down the tube, A is consumed, its concentration drops, and the rate slows. This distributed nature means we need a <em>differential</em> mole balance on a small slice ΔV.</p>
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <img src="/pfr-diagram.jpg" alt="PFR Diagram showing tubular reactor with inlet F_A0, outlet F_A, and velocity profiles" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <div className="cre-steps">
            <div className="cre-step">
              <div className="cre-step-num">1</div>
              <div className="cre-step-content">
                <h4>Mole balance on a differential slice ΔV</h4>
                <p>Flow in at position V minus flow out at V+ΔV plus generation in ΔV = 0 (steady state)</p>
                <p>F_A|_V − F_A|_(V+ΔV) + r_A · ΔV = 0</p>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">2</div>
              <div className="cre-step-content">
                <h4>Divide by ΔV and take limit as ��V → 0</h4>
                <p>This gives the differential form:</p>
                <div className="cre-eq-box">
                  <div className="cre-eq-label">PFR Design Equation (Differential)</div>
                  <div className="cre-eq-main"><BlockMath math="\frac{dF_A}{dV} = r_A" /></div>
                </div>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">3</div>
              <div className="cre-step-content">
                <h4>Integral form — volume required</h4>
                <div className="cre-eq-box">
                  <div className="cre-eq-label">PFR Design Equation (Integral)</div>
                  <div className="cre-eq-main"><BlockMath math="V = \int_{F_{A0}}^{F_A} \frac{dF_A}{r_A}" /></div>
                  <div className="cre-eq-note">This is the volume needed to reduce molar flow rate from F_A0 to F_A.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION: PBR */}
        <section className="cre-section" id="pbr">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Chapter 1</div>
          <div className="cre-section-label">Reactor Type 4</div>
          <h2>Packed Bed Reactor (PBR)</h2>
          <div className="cre-section-divider"></div>
          <p>A <strong>Packed Bed Reactor (PBR)</strong> is like a PFR, but the reactor is filled with solid catalyst particles. The reaction happens on the catalyst surface, so the rate is expressed <em>per unit mass of catalyst</em> (mol/kg_cat/s), denoted <code className="cre-inline-code">-r'_A</code>. The independent variable is catalyst weight W, not volume V.</p>
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <img src="/pbr-diagram.jpg" alt="PBR Diagram showing packed bed of catalyst particles with inlet F_A0, outlet F_A, and weight W labeled" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <h3>Why Weight Instead of Volume?</h3>
          <p>In heterogeneous catalysis, the reaction occurs on the catalyst surface. More catalyst weight = more active sites = more reaction. The amount of catalyst (kg) is more relevant than the void volume the gas occupies.</p>
          <div className="cre-eq-box">
            <div className="cre-eq-label">PBR Design Equations</div>
            <div className="cre-eq-main">
              <BlockMath math="\text{Differential:} \quad \frac{dF_A}{dW} = r'_A" />
              <BlockMath math="\text{Integral:} \quad W = \int_{F_{A0}}^{F_A} \frac{dF_A}{r'_A}" />
            </div>
            <div className="cre-eq-note">W = catalyst weight needed to reduce molar flow rate from F_A0 to F_A. Note: r'_A has a prime (per mass of catalyst).</div>
          </div>
          <div className="cre-callout cre-callout--key">
            <p><strong>PBR vs. PFR:</strong> The equations are identical in form — just replace V with W and r_A with r'_A. The PBR equation accounts for the fact that reaction happens on catalyst, not in the gas phase.</p>
          </div>
        </section>

        {/* SECTION: Reactor Summary */}
        <section className="cre-section" id="reactor-summary">
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Summary</div>
          <div className="cre-section-label">Summary</div>
          <h2>Reactor Mole Balances — Summary Table</h2>
          <div className="cre-section-divider"></div>
          <p>The following table summarizes the mole balance equations for all four major reactor types for the general reaction A → B. These are the starting point for every reactor design calculation.</p>
          <table className="cre-table">
            <thead>
              <tr>
                <th>Reactor</th>
                <th>Differential Form</th>
                <th>Algebraic Form</th>
                <th>Integral Form</th>
                <th>Characteristic</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Batch (BR)</td>
                <td><code className="cre-inline-code">dN_A/dt = r_A·V</code></td>
                <td>—</td>
                <td><code className="cre-inline-code">t = ∫ dN_A/(−r_A·V)</code></td>
                <td>No flow; time is variable</td>
              </tr>
              <tr>
                <td>CSTR</td>
                <td>—</td>
                <td><code className="cre-inline-code">V = (F_A0−F_A)/(−r_A)</code></td>
                <td>—</td>
                <td>Algebraic; rate at exit conditions</td>
              </tr>
              <tr>
                <td>PFR</td>
                <td><code className="cre-inline-code">dF_A/dV = r_A</code></td>
                <td>—</td>
                <td><code className="cre-inline-code">V = ∫ dF_A/r_A</code></td>
                <td>Variable along length; needs integration</td>
              </tr>
              <tr>
                <td>PBR</td>
                <td><code className="cre-inline-code">dF_A/dW = r'_A</code></td>
                <td>—</td>
                <td><code className="cre-inline-code">W = ∫ dF_A/r'_A</code></td>
                <td>Like PFR but per catalyst weight</td>
              </tr>
            </tbody>
          </table>
          <div className="cre-reactor-grid">
            <div className="cre-reactor-card">
              <div className="cre-reactor-header cre-reactor-header--batch">Batch Reactor</div>
              <div className="cre-reactor-body">
                <p>Closed system. Time-dependent. Used for small-scale production, pharmaceuticals, specialty chemicals.</p>
                <div className="cre-reactor-eq"><span>dN_A/dt</span> = r_A · V</div>
              </div>
            </div>
            <div className="cre-reactor-card">
              <div className="cre-reactor-header cre-reactor-header--cstr">CSTR</div>
              <div className="cre-reactor-body">
                <p>Continuous, perfectly mixed. Uniform concentration = exit concentration. Liquid phase preferred.</p>
                <div className="cre-reactor-eq">V = <span>(F_A0 − F_A)</span> / (−r_A)</div>
              </div>
            </div>
            <div className="cre-reactor-card">
              <div className="cre-reactor-header cre-reactor-header--pfr">PFR</div>
              <div className="cre-reactor-body">
                <p>Continuous tube, no axial mixing. Concentration varies along length. Gas or liquid phase.</p>
                <div className="cre-reactor-eq">dF_A/dV = <span>r_A</span></div>
              </div>
            </div>
            <div className="cre-reactor-card">
              <div className="cre-reactor-header cre-reactor-header--pbr">PBR</div>
              <div className="cre-reactor-body">
                <p>Like PFR but packed with solid catalyst. Rate per catalyst mass. Common for gas-phase reactions.</p>
                <div className="cre-reactor-eq">dF_A/dW = <span>r'_A</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* LECTURE 8 — PRESSURE DROP */}
        <div className="cre-lecture-divider">
          <div className="cre-lecture-label">Lecture 8</div>
          <h2>Pressure Drop in Packed Bed Reactors</h2>
          <p>As gas flows through the catalyst bed, friction causes pressure to drop. This pressure drop changes gas density and concentration — directly reducing reaction rates and conversion. Ignoring it leads to incorrect reactor designs.</p>
        </div>

        {/* Real-World Application: Propylene Glycol */}
        <section className="cre-section" style={{ backgroundColor: 'var(--t-panel)', padding: '20px', borderRadius: '8px', marginBottom: '40px' }}>
          <div className="cre-lec-badge cre-lec-badge--lec1">Lecture 1 · Real-World Application</div>
          <h3 style={{ marginTop: '0' }}>Production of Propylene Glycol in an Adiabatic CSTR</h3>
          <p>A practical example of applying the CRE algorithm: propylene glycol is produced by the hydrolysis of propylene oxide in the presence of H₂SO₄ catalyst:</p>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Reaction</div>
            <div className="cre-eq-main"><BlockMath math="CH_2-CH-CH_3 + H_2O \xrightarrow{H_2SO_4} CH_2-CH-CH_3 \\ O \quad \quad \quad \quad \quad \quad \quad OH \quad OH" /></div>
          </div>
          <div style={{ margin: '24px 0', textAlign: 'center' }}>
            <img src="/propylene-glycol-reaction.jpg" alt="Propylene Glycol Reaction showing propylene oxide and water reacting with H2SO4 catalyst to form propylene glycol" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <p>This example demonstrates applying Blocks 1-4 (Mole Balance, Rate Law, Stoichiometry, and Combine & Solve) along with heat effects to size a 300-gallon CSTR, achieving 84% conversion at 614°R.</p>
        </section>

        {/* SECTION: Why Pressure Drop Matters */}
        <section className="cre-section" id="pd-intro">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8</div>
          <div className="cre-section-label">Motivation</div>
          <h2>Why Pressure Drop Matters in PBRs</h2>
          <div className="cre-section-divider"></div>
          <p>In a Packed Bed Reactor, gas flows through a bed of catalyst particles. As the gas moves through, it experiences <strong>frictional resistance</strong> from the packing. This causes the pressure to drop continuously along the bed. Since gas-phase concentration depends directly on pressure (<code className="cre-inline-code">C = P/RT</code> for ideal gas), a drop in pressure causes a drop in concentration — which <em>slows the reaction rate</em>.</p>
          <div className="cre-card-grid">
            <div className="cre-card">
              <div className="cre-card-icon">💨</div>
              <h4>Liquid Phase Reactions</h4>
              <p>Pressure drop does <strong>NOT</strong> affect concentrations in liquid phase reactions (liquids are nearly incompressible). We can ignore it for liquid systems.</p>
            </div>
            <div className="cre-card">
              <div className="cre-card-icon">🔥</div>
              <h4>Gas Phase Reactions</h4>
              <p>Pressure drop <strong>significantly affects</strong> gas-phase concentrations. Lower P → lower concentration → slower rate → lower conversion. Must be included in design.</p>
            </div>
          </div>
          <div className="cre-callout cre-callout--warn">
            <p><strong>Engineering Reality:</strong> A PBR designed without accounting for pressure drop will be undersized. The actual conversion will be lower than predicted because the gas expands as pressure drops, diluting the reactants.</p>
          </div>
        </section>

        {/* SECTION: Gas Phase Concentrations */}
        <section className="cre-section" id="gas-conc">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8</div>
          <div className="cre-section-label">Stoichiometry Block</div>
          <h2>Gas Phase Concentrations with Pressure Drop</h2>
          <div className="cre-section-divider"></div>
          <p>For an ideal gas, the volumetric flow rate changes with temperature, pressure, and total moles. The key relationships are:</p>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Concentration — Flow System Definition</div>
            <div className="cre-eq-main"><BlockMath math="C_A = \frac{F_A}{\upsilon}" /></div>
            <div className="cre-eq-note">F_A = molar flow rate of A (mol/s) &nbsp;|&nbsp; υ = volumetric flow rate (dm³/s)</div>
          </div>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Volumetric Flow Rate — Gas Phase</div>
            <div className="cre-eq-main"><BlockMath math="\upsilon = \upsilon_0 \cdot (1 + \varepsilon X) \cdot \frac{T}{T_0} \cdot \frac{P_0}{P}" /></div>
            <div className="cre-eq-note">
              υ₀ = inlet volumetric flow rate &nbsp;|&nbsp; X = conversion<br />
              ε = (δ · y_A0) where δ = change in total moles per mole of A reacted<br />
              T₀, P₀ = inlet temperature and pressure &nbsp;|&nbsp; T, P = local temperature and pressure
            </div>
          </div>
          <h3>Understanding ε (epsilon)</h3>
          <p>ε accounts for the change in total molar flow as the reaction proceeds. For <code className="cre-inline-code">A → B</code>, no change in moles, so ε = 0. For <code className="cre-inline-code">A → 2B</code>, moles increase, so ε &gt; 0 (gas expands). For <code className="cre-inline-code">A + B → C</code>, moles decrease, so ε &lt; 0 (gas contracts).</p>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Concentration of A — Gas Phase with Pressure Drop (Isothermal)</div>
            <div className="cre-eq-main"><BlockMath math="C_A = C_{A0} \cdot \frac{1-X}{1+\varepsilon X} \cdot \frac{P}{P_0}" /></div>
          </div>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Concentration of B — Stoichiometric Feed (Θ_B = b/a)</div>
            <div className="cre-eq-main"><BlockMath math="C_B = C_{A0} \cdot \frac{\Theta_B - \frac{b}{a}X}{1+\varepsilon X} \cdot \frac{P}{P_0}" /></div>
          </div>
          <div className="cre-callout cre-callout--key">
            <p><strong>The P/P₀ Term:</strong> This is the dimensionless pressure ratio, often written as <code className="cre-inline-code">p = P/P₀</code>. When there is no pressure drop, p = 1. As gas flows through the bed, p decreases from 1 toward 0, reducing C_A proportionally.</p>
          </div>
        </section>

        {/* SECTION: Ergun Equation */}
        <section className="cre-section" id="ergun">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8</div>
          <div className="cre-section-label">Pressure Drop Correlation</div>
          <h2>The Ergun Equation</h2>
          <div className="cre-section-divider"></div>
          <p>The <strong>Ergun Equation</strong> is the fundamental correlation for pressure drop through a packed bed. It has two terms representing different flow regimes:</p>
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <img src="/ergun-equation-diagram.jpg" alt="Ergun Equation Diagram showing pressure drop along packed bed height with particle porosity and fluid velocity effects" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Ergun Equation — General Form</div>
            <div className="cre-eq-main">
              <BlockMath math="-\frac{dP}{dz} = \frac{G(1-\phi)}{\rho_0 \, g_c \, D_p \, \phi^3} \left[\frac{150(1-\phi)\mu}{D_p} + 1.75G\right]" />
            </div>
            <div className="cre-eq-note">
              G = ρu = superficial mass velocity (kg/m²·s) &nbsp;|&nbsp; φ = porosity (void fraction of bed)<br />
              ρ₀ = inlet gas density &nbsp;|&nbsp; g_c = conversion factor &nbsp;|&nbsp; D_p = particle diameter &nbsp;|&nbsp; μ = viscosity
            </div>
          </div>
          <h3>The Two Flow Regimes</h3>
          <div className="cre-card-grid">
            <div className="cre-card">
              <div className="cre-card-icon">🌊</div>
              <h4>Laminar (Blake-Kozeny) — Term 1</h4>
              <p>Dominates at low Reynolds numbers (slow flow, small particles, viscous gas). Pressure drop ∝ μ·G (linear in velocity). <br />Scales as <code className="cre-inline-code">α ~ G / (D_p² · P₀²)</code></p>
            </div>
            <div className="cre-card">
              <div className="cre-card-icon">🌪️</div>
              <h4>Turbulent (Burke-Plummer) — Term 2</h4>
              <p>Dominates at high Reynolds numbers (fast flow, large particles). Pressure drop ∝ G² (quadratic in velocity). <br />Scales as <code className="cre-inline-code">α ~ G² / (D_p · P₀²)</code></p>
            </div>
          </div>
          <h3>Key Variable: β₀</h3>
          <p>We collect all the constant terms from the Ergun equation into a single parameter β₀:</p>
          <div className="cre-eq-box">
            <div className="cre-eq-label">Definition of β₀</div>
            <div className="cre-eq-main"><BlockMath math="\beta_0 = \frac{G(1-\phi)}{\rho_0 \, g_c \, D_p \, \phi^3} \left[\frac{150(1-\phi)\mu}{D_p} + 1.75G\right]" /></div>
            <div className="cre-eq-note">β₀ has units of pressure per length (Pa/m or atm/m). It is a constant evaluated at inlet conditions.</div>
          </div>
        </section>

        {/* SECTION: dp/dW Derivation */}
        <section className="cre-section" id="pd-deriv">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8</div>
          <div className="cre-section-label">Key Derivation</div>
          <h2>Pressure Drop Equation for PBRs: dp/dW</h2>
          <div className="cre-section-divider"></div>
          <p>We need to express pressure drop in terms of <em>catalyst weight W</em> (not bed length z), because our PBR mole balance uses W as the independent variable. We also need to account for the fact that gas density changes as conversion and pressure change.</p>
          <div className="cre-steps">
            <div className="cre-step">
              <div className="cre-step-num">1</div>
              <div className="cre-step-content">
                <h4>Variable density substitution</h4>
                <p>The local gas density ρ changes with T, P, and total molar flow. Using constant mass flow: ρ = ρ₀ · (F_T/F_T0) · (T₀/T) · (P/P₀)⁻¹ · ... (via ideal gas relations)</p>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">2</div>
              <div className="cre-step-content">
                <h4>Convert dz to dW using catalyst bulk density</h4>
                <p>W = z · A_c · ρ_b = z · A_c · (1−φ) · ρ_c &nbsp;&nbsp; (where A_c = cross-section area)</p>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">3</div>
              <div className="cre-step-content">
                <h4>Define dimensionless pressure p = P/P₀ and parameter α</h4>
                <div className="cre-eq-box">
                  <div className="cre-eq-label">Definition of α (Pressure Drop Parameter)</div>
                  <div className="cre-eq-main"><BlockMath math="\alpha = \frac{2\beta_0}{P_0 \cdot A_c \cdot (1-\phi) \cdot \rho_c}" /></div>
                  <div className="cre-eq-note">α has units of kg⁻¹. It captures how quickly pressure drops per kg of catalyst. Larger α → steeper pressure drop.</div>
                </div>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">4</div>
              <div className="cre-step-content">
                <h4>Final pressure drop ODE</h4>
                <div className="cre-eq-box">
                  <div className="cre-eq-label">General Pressure Drop Equation (use for multiple reactions / heat effects)</div>
                  <div className="cre-eq-main"><BlockMath math="\frac{dp}{dW} = -\frac{\alpha}{2p} \cdot \frac{F_T}{F_{T0}} \cdot \frac{T}{T_0}" /></div>
                </div>
                <div className="cre-eq-box">
                  <div className="cre-eq-label">Isothermal Case (T = T₀)</div>
                  <div className="cre-eq-main"><BlockMath math="\frac{dp}{dW} = -\frac{\alpha}{2p}(1 + \varepsilon X)" /></div>
                  <div className="cre-eq-note">Since F_T/F_T0 = (1 + εX) for a single reaction at isothermal conditions.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="cre-callout cre-callout--warn">
            <p><strong>Coupled ODEs:</strong> This dp/dW equation must be solved <em>simultaneously</em> with the mole balance dX/dW. They are coupled — X affects p (through ε), and p affects X (through C_A). Generally requires an ODE solver like Polymath.</p>
          </div>
        </section>

        {/* SECTION: Special Case ε=0 */}
        <section className="cre-section" id="eps-zero">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8</div>
          <div className="cre-section-label">Analytical Solution</div>
          <h2>Special Case: ε = 0, Isothermal</h2>
          <div className="cre-section-divider"></div>
          <p>When the reaction has <strong>no change in total moles</strong> (ε = 0) and the reactor operates <strong>isothermally</strong> (T = T₀), the pressure drop equation decouples and can be integrated analytically.</p>
          <h3>Analytical Solution for p(W)</h3>
          <div className="cre-steps">
            <div className="cre-step">
              <div className="cre-step-num">1</div>
              <div className="cre-step-content">
                <h4>Simplified ODE (ε = 0, isothermal)</h4>
                <p><code className="cre-inline-code">dp/dW = −α/(2p)</code></p>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">2</div>
              <div className="cre-step-content">
                <h4>Separate variables</h4>
                <p><code className="cre-inline-code">2p dp = −α dW</code></p>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">3</div>
              <div className="cre-step-content">
                <h4>Integrate with initial condition W=0, p=1</h4>
                <p><code className="cre-inline-code">p² = 1 − αW</code></p>
              </div>
            </div>
            <div className="cre-step">
              <div className="cre-step-num">4</div>
              <div className="cre-step-content">
                <h4>Final analytical result</h4>
                <div className="cre-eq-box">
                  <div className="cre-eq-label">Pressure Profile — ε=0, Isothermal</div>
                  <div className="cre-eq-main"><BlockMath math="p = \frac{P}{P_0} = \sqrt{1 - \alpha W}" /></div>
                  <div className="cre-eq-note">
                    Pressure decreases as a square-root function of catalyst weight.<br />
                    At W = 0: p = 1 (inlet pressure) &nbsp;|&nbsp; At W = 1/α: p = 0 (complete pressure drop — design must avoid this!)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION: Effect on Profiles */}
        <section className="cre-section" id="profiles">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8</div>
          <div className="cre-section-label">Physical Insight</div>
          <h2>Effect of Pressure Drop on Conversion Profiles</h2>
          <div className="cre-section-divider"></div>
          <p>For a gas-phase reaction in a PBR, pressure drop has a direct negative effect on conversion. This can be seen by comparing the X vs. W profile with and without ΔP:</p>
          <table className="cre-table">
            <thead><tr><th>Scenario</th><th>X at W = W_final</th><th>C_A behavior</th><th>Reaction rate behavior</th></tr></thead>
            <tbody>
              <tr><td>No pressure drop (p = 1)</td><td>X_max (highest)</td><td>Decreases with X only</td><td>Decreases only due to consumption</td></tr>
              <tr><td>With pressure drop (p &lt; 1)</td><td>X_actual &lt; X_max</td><td>Decreases with both X and p drop</td><td>Decreases faster — double effect</td></tr>
              <tr><td>High α (severe ΔP)</td><td>X &lt;&lt; X_max</td><td>Severely reduced</td><td>Rate may approach zero before bed end</td></tr>
            </tbody>
          </table>
          <div className="cre-callout cre-callout--key">
            <p><strong>Bottom line:</strong> Pressure drop always <em>hurts</em> conversion. A reactor designed assuming no ΔP will deliver less conversion than expected in reality. The larger the value of α, the worse the effect.</p>
          </div>
        </section>

        {/* SECTION: Example 1 */}
        <section className="cre-section" id="example1">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8 · Example 1</div>
          <div className="cre-section-label">Worked Example</div>
          <h2>Example 1: Gas Phase PBR with δ = 0 (Analytical)</h2>
          <div className="cre-section-divider"></div>
          <div className="cre-example-box">
            <div className="cre-example-tag">Problem Statement</div>
            <h4>Reaction: A + B → 2C</h4>
            <p>Equimolar feed of A and B (C_A0 = C_B0). Find conversion X at W = 100 kg.</p>
            <p>Given: k_A = 1.5 dm⁶/mol/kg/min &nbsp;|&nbsp; α = 0.0099 kg⁻¹ &nbsp;|&nbsp; F_A0 known</p>
          </div>
          <h3>Solution — Step by Step</h3>
          <div className="cre-steps">
            <div className="cre-step"><div className="cre-step-num">1</div><div className="cre-step-content"><h4>Verify δ = 0 (so ε = 0)</h4><p>Stoichiometry: 1 mol A + 1 mol B → 2 mol C. Total moles: 2 reactant → 2 product. Change = 0. So δ = 0, ε = 0. ✓</p></div></div>
            <div className="cre-step"><div className="cre-step-num">2</div><div className="cre-step-content"><h4>Mole Balance</h4><p><code className="cre-inline-code">dX/dW = −r'_A / F_A0</code></p></div></div>
            <div className="cre-step"><div className="cre-step-num">3</div><div className="cre-step-content"><h4>Rate Law (second order in A, B)</h4><p><code className="cre-inline-code">−r'_A = k · C_A · C_B</code></p></div></div>
            <div className="cre-step"><div className="cre-step-num">4</div><div className="cre-step-content"><h4>Stoichiometry (ε=0, isothermal)</h4><p>C_A = C_B = C_A0 (1−X) p &nbsp;&nbsp; where p = P/P₀</p></div></div>
            <div className="cre-step"><div className="cre-step-num">5</div><div className="cre-step-content"><h4>Pressure (ε=0 analytical result)</h4><div className="cre-eq-box"><div className="cre-eq-main"><BlockMath math="p = \sqrt{1 - \alpha W} \implies p^2 = 1 - \alpha W" /></div></div></div></div>
            <div className="cre-step"><div className="cre-step-num">6</div><div className="cre-step-content"><h4>Combine: substitute C_A, C_B, and p into mole balance</h4><div className="cre-eq-box"><div className="cre-eq-main"><BlockMath math="-r'_A = k C_{A0}^2 (1-X)^2 p^2 = k C_{A0}^2 (1-X)^2 (1-\alpha W)" /><BlockMath math="\frac{dX}{dW} = \frac{k C_{A0}^2}{F_{A0}} (1-X)^2 (1-\alpha W)" /></div></div></div></div>
            <div className="cre-step"><div className="cre-step-num">7</div><div className="cre-step-content"><h4>Separate variables and integrate analytically</h4><div className="cre-eq-box"><div className="cre-eq-main"><BlockMath math="\frac{dX}{(1-X)^2} = \frac{k C_{A0}^2}{F_{A0}} (1-\alpha W)\, dW" /><BlockMath math="\frac{X}{1-X} = \frac{k C_{A0}^2}{F_{A0}} \left(W - \frac{\alpha W^2}{2}\right)" /></div></div></div></div>
            <div className="cre-step"><div className="cre-step-num">8</div><div className="cre-step-content"><h4>Results at W = 100 kg</h4><div className="cre-eq-box"><div className="cre-eq-main">X = 0.6 &nbsp;&nbsp; (with pressure drop, α = 0.0099)<br />X = 0.75 &nbsp; (without pressure drop, α = 0)</div><div className="cre-eq-note">Pressure drop reduces conversion from 75% to 60% — a significant 20% decrease in performance!</div></div></div></div>
          </div>
        </section>

        {/* SECTION: Example 2 */}
        <section className="cre-section" id="example2">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8 · Example 2</div>
          <div className="cre-section-label">Worked Example (Numerical)</div>
          <h2>Example 2: Gas Phase PBR with δ ≠ 0 (Polymath)</h2>
          <div className="cre-section-divider"></div>
          <div className="cre-example-box">
            <div className="cre-example-tag">Problem Statement</div>
            <h4>Reaction: A + 2B → C</h4>
            <p>Stoichiometric feed of A and B. Plot X and p = P/P₀ vs. W up to 100 kg.</p>
            <p>Given: P₀ = 10 atm &nbsp;|&nbsp; F_A0 = 2 mol/min &nbsp;|&nbsp; C_A0 = 0.2 mol/dm³ &nbsp;|&nbsp; k_A = 6 dm⁹/mol²/kg/min &nbsp;|&nbsp; α = 0.02 kg⁻¹</p>
          </div>
          <h3>Solution Approach</h3>
          <div className="cre-steps">
            <div className="cre-step"><div className="cre-step-num">1</div><div className="cre-step-content"><h4>Calculate δ and ε</h4><p>A + 2B → C: products = 1 mol, reactants = 3 mol, change = 1−3 = −2.</p><p>δ = (1−2−1)/1 = −2 for one mole of A basis. With equimolar feed y_A0 = 1/3:</p><div className="cre-eq-box"><div className="cre-eq-main"><BlockMath math="\varepsilon = y_{A0} \cdot \delta = \tfrac{1}{3} \times (-2) = -\tfrac{2}{3} \approx -0.667" /></div></div><p>Negative ε means the gas <em>contracts</em> as reaction proceeds.</p></div></div>
            <div className="cre-step"><div className="cre-step-num">2</div><div className="cre-step-content"><h4>Set up equations (cannot separate variables analytically since ε ≠ 0)</h4><div className="cre-eq-box"><div className="cre-eq-label">Coupled ODE System for Polymath</div><div className="cre-eq-main"><BlockMath math="\frac{dX}{dW} = \frac{-r_A}{F_{A0}}" /><BlockMath math="\frac{dp}{dW} = -\frac{\alpha(1+\varepsilon X)}{2p}" /><BlockMath math="C_A = C_{A0}\frac{1-X}{1+\varepsilon X} \cdot p \qquad C_B = C_{A0}\frac{\Theta_B - 2X}{1+\varepsilon X} \cdot p" /><BlockMath math="r_A = -k\,C_A\,C_B^2" /></div></div></div></div>
            <div className="cre-step"><div className="cre-step-num">3</div><div className="cre-step-content"><h4>Initial and final values</h4><p>Initial: W=0, X=0, p=1 &nbsp;&nbsp;|&nbsp;&nbsp; Final: W=100, X=?, p=?</p></div></div>
            <div className="cre-step"><div className="cre-step-num">4</div><div className="cre-step-content"><h4>Polymath (RKF45 solver) results</h4><div className="cre-eq-box"><div className="cre-eq-main">At W = 100 kg:<br />X = 0.859 &nbsp;&nbsp; (high conversion — negative ε helps!)<br />p = 0.115 &nbsp;&nbsp; (only ~11.5% of inlet pressure remains!)</div><div className="cre-eq-note">Even though conversion looks good, the severe pressure drop (p → 0.115) is a major engineering concern. At some point, the pressure drop is so severe it could become impractical.</div></div></div></div>
          </div>
          <div className="cre-callout cre-callout--tip">
            <p><strong>Why does ε &lt; 0 help conversion here?</strong> When ε is negative (moles decrease), the gas contracts as reaction proceeds. This <em>increases</em> concentration (fewer moles in same volume), partially counteracting the dilution effect of the pressure drop. The net effect depends on the relative magnitudes.</p>
          </div>
        </section>

        {/* SECTION: Engineering Analysis */}
        <section className="cre-section" id="eng-analysis">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8</div>
          <div className="cre-section-label">Design Tool</div>
          <h2>Engineering Analysis of Pressure Drop</h2>
          <div className="cre-section-divider"></div>
          <p>The α parameter governs how severely pressure drops in a PBR. We can analyze how design changes affect α using simplified forms of the Ergun equation for each flow regime.</p>
          <h3>How Does α Scale?</h3>
          <div className="cre-card-grid">
            <div className="cre-card"><div className="cre-card-icon">🔵</div><h4>Laminar Flow (Term 1 dominates)</h4><p><code className="cre-inline-code">α ~ G / (A_c · D_p² · P₀²)</code></p><p>Halving particle size → 4× larger pressure drop. Doubling inlet pressure → 4× smaller pressure drop.</p></div>
            <div className="cre-card"><div className="cre-card-icon">🔴</div><h4>Turbulent Flow (Term 2 dominates)</h4><p><code className="cre-inline-code">α ~ G² / (A_c · D_p · P₀²)</code></p><p>Doubling flow rate → 4× larger pressure drop. Halving particle diameter → 2× larger pressure drop.</p></div>
          </div>
          <h3>Case Study: Changing D_p and P₀</h3>
          <div className="cre-example-box">
            <div className="cre-example-tag">Engineering Analysis Example (Laminar Flow)</div>
            <h4>Decrease D_p by 4× and increase P₀ by 3×</h4>
            <p>New particle: D_P2 = ¼ D_P1 &nbsp;&nbsp;|&nbsp;&nbsp; New inlet pressure: P₀2 = 3 P₀1</p>
            <div className="cre-eq-box">
            <div className="cre-eq-main"><BlockMath math="\alpha_2 = \alpha_1 \cdot \left(\frac{D_{P1}}{D_{P2}}\right)^2 \cdot \left(\frac{P_{01}}{P_{02}}\right)^2 = \alpha_1 \cdot (4)^2 \cdot \left(\tfrac{1}{3}\right)^2 = \alpha_1 \cdot \frac{16}{9} \approx 1.78\,\alpha_1" /></div>
              <div className="cre-eq-note">Despite tripling the inlet pressure (which helps), the 4× smaller particles dominate, giving ~78% larger pressure drop. The particle size effect is very strong!</div>
            </div>
          </div>
          <h3>Design Guidelines for Minimizing Pressure Drop</h3>
          <ul>
            <li><strong>Larger catalyst particles</strong> → lower ΔP (but reduces external surface area and may hurt mass transfer)</li>
            <li><strong>Higher inlet pressure P₀</strong> → significantly reduces α (scales as 1/P₀²)</li>
            <li><strong>Larger reactor cross-section A_c</strong> → lower superficial velocity G → lower ΔP</li>
            <li><strong>Multiple parallel beds</strong> → each carries less flow, lower ΔP per bed</li>
            <li><strong>Shorter beds</strong> → less total ΔP (but requires wider cross-section to maintain W)</li>
          </ul>
        </section>

        {/* SECTION: Lecture 8 Summary */}
        <section className="cre-section" id="summary8">
          <div className="cre-lec-badge cre-lec-badge--lec8">Lecture 8 · Summary</div>
          <div className="cre-section-label">Summary</div>
          <h2>Lecture 8 Key Equations Summary</h2>
          <div className="cre-section-divider"></div>
          <table className="cre-table">
            <thead><tr><th>Equation</th><th>Form</th><th>When to Use</th></tr></thead>
            <tbody>
              <tr><td>C_A (gas, isothermal)</td><td><code className="cre-inline-code">C_A0·(1−X)/(1+εX)·(P/P₀)</code></td><td>Always for gas-phase stoichiometry</td></tr>
              <tr><td>dp/dW (general)</td><td><code className="cre-inline-code">−(α/2p)·(F_T/F_T0)·(T/T₀)</code></td><td>Multiple reactions, heat effects</td></tr>
              <tr><td>dp/dW (isothermal)</td><td><code className="cre-inline-code">−(α/2p)·(1+εX)</code></td><td>Single isothermal reaction</td></tr>
              <tr><td>p(W) analytical</td><td><code className="cre-inline-code">(1−αW)^(1/2)</code></td><td>Only when ε=0 AND isothermal</td></tr>
              <tr><td>α (pressure param)</td><td><code className="cre-inline-code">2β₀ / [P₀·A_c·(1−φ)·ρ_c]</code></td><td>Computing α from physical parameters</td></tr>
              <tr><td>Combined ODE (ε=0)</td><td><code className="cre-inline-code">X/(1−X) = (kC²_A0/F_A0)·(W − αW²/2)</code></td><td>δ=0 isothermal: analytical solution</td></tr>
            </tbody>
          </table>
          <div className="cre-callout cre-callout--warn">
            <p><strong>Solve Strategy:</strong> If ε = 0 and isothermal → analytical solution possible. If ε ≠ 0 OR non-isothermal → must use Polymath (or another ODE solver) to integrate coupled dX/dW and dp/dW simultaneously.</p>
          </div>
        </section>

        {/* SECTION: CRE Algorithm */}
        <section className="cre-section" id="algorithm">
          <div className="cre-section-label">Reference</div>
          <h2>The CRE Algorithm — Full Picture</h2>
          <div className="cre-section-divider"></div>
          <p>Every reactor design problem in CRE is solved by going through these blocks systematically. Do not skip steps.</p>
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <img src="/cre-algorithm-flowchart.jpg" alt="CRE Algorithm Flowchart showing building blocks: Mole Balance, Rate Laws, Stoichiometry, and Combine & Solve connected with arrows" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t-border)' }} />
          </div>
          <div className="cre-steps">
            <div className="cre-step"><div className="cre-step-num">1</div><div className="cre-step-content"><h4>Mole Balance — Choose your reactor type</h4><p>Write the design equation: dX/dW = −r'_A / F_A0 (for PBR) or dX/dV = −r_A / F_A0 (for PFR)</p></div></div>
            <div className="cre-step"><div className="cre-step-num">2</div><div className="cre-step-content"><h4>Rate Law — From experiments or literature</h4><p>Express −r'_A in terms of concentrations. Example: −r'_A = k·C_A·C_B</p></div></div>
            <div className="cre-step"><div className="cre-step-num">3</div><div className="cre-step-content"><h4>Stoichiometry — Express concentrations in terms of X</h4><p>Gas phase: C_A = C_A0·(1−X)/(1+εX)·p &nbsp;&nbsp; Liquid phase: C_A = C_A0·(1−X)</p><p>For gas with pressure drop, also include: dp/dW = −(α/2p)·(1+εX)</p></div></div>
            <div className="cre-step"><div className="cre-step-num">4</div><div className="cre-step-content"><h4>Combine — Substitute into mole balance</h4><p>You now have dX/dW = f(X, p, W). Solve analytically if possible, or use Polymath/ODE solver.</p></div></div>
            <div className="cre-step"><div className="cre-step-num">5</div><div className="cre-step-content"><h4>Evaluate — Extract engineering insight</h4><p>Plot X, p, C_A, −r_A vs. W. Check if ΔP is acceptable. Consider design modifications.</p></div></div>
          </div>
        </section>

        {/* SECTION: Key Symbols */}
        <section className="cre-section" id="key-symbols">
          <div className="cre-section-label">Reference</div>
          <h2>Key Symbols &amp; Variables</h2>
          <div className="cre-section-divider"></div>
          <table className="cre-table">
            <thead><tr><th>Symbol</th><th>Meaning</th><th>Typical Units</th></tr></thead>
            <tbody>
              <tr><td>X</td><td>Conversion of A</td><td>dimensionless (0 to 1)</td></tr>
              <tr><td>p = P/P₀</td><td>Dimensionless pressure ratio</td><td>dimensionless</td></tr>
              <tr><td>ε (epsilon)</td><td>Fractional volume change at complete conversion</td><td>dimensionless</td></tr>
              <tr><td>δ (delta)</td><td>Change in total moles per mole of A reacted</td><td>dimensionless</td></tr>
              <tr><td>α (alpha)</td><td>Pressure drop parameter (how fast P drops with W)</td><td>kg⁻¹</td></tr>
              <tr><td>β₀</td><td>Pressure drop per length at inlet conditions</td><td>Pa/m or atm/m</td></tr>
              <tr><td>−r_A</td><td>Rate of disappearance of A per unit volume</td><td>mol/dm³/s</td></tr>
              <tr><td>−r'_A</td><td>Rate of disappearance of A per mass of catalyst</td><td>mol/kg_cat/s</td></tr>
              <tr><td>F_A0</td><td>Inlet molar flow rate of A</td><td>mol/s or mol/min</td></tr>
              <tr><td>C_A0</td><td>Inlet concentration of A</td><td>mol/dm³</td></tr>
              <tr><td>W</td><td>Catalyst weight</td><td>kg</td></tr>
              <tr><td>G</td><td>Superficial mass velocity</td><td>kg/m²/s</td></tr>
              <tr><td>D_p</td><td>Catalyst particle diameter</td><td>m</td></tr>
              <tr><td>φ (phi)</td><td>Void fraction (porosity) of packed bed</td><td>dimensionless</td></tr>
              <tr><td>ρ_c</td><td>Solid catalyst density</td><td>kg/m³</td></tr>
              <tr><td>ρ_b</td><td>Bulk density of packed bed</td><td>kg/m³</td></tr>
              <tr><td>Θ_B</td><td>Ratio of inlet molar flow: F_B0/F_A0</td><td>dimensionless</td></tr>
              <tr><td>k</td><td>Rate constant (temperature-dependent via Arrhenius)</td><td>varies with order</td></tr>
            </tbody>
          </table>
        </section>

        <div className="cre-footer">
          Based on: H. Scott Fogler, "Elements of Chemical Reaction Engineering," 6th Edition, University of Michigan · Course notes compiled for educational use
        </div>
        </div>
      </main>
    </div>

        </div>   {/* end scrollable content */}
      </div>     {/* end rightCol */}
    </div>       
  );
};

export default ChemicalReactionEngineeringNotes;